import { useState } from "react"
import { Link, Navigate, useNavigate } from "react-router-dom"
import { Mail } from "lucide-react"
import { toast } from "sonner"

import { AuthCard, AuthLegalNotice, authButtonClass, authFieldClass, authOutlineButtonClass } from "@/components/auth/AuthCard"
import { AuthLayout } from "@/components/layouts/AuthLayout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { FullPageLoader } from "@/components/ui/full-page-loader"
import { useAuth } from "@/contexts/AuthContext"
import { useAppSession } from "@/hooks/useAppSession"
import logo from "@/assets/logo.png"

export function LoginPage() {
  const navigate = useNavigate()
  const { requestMagicLink, verifyMagicLink } = useAuth()
  const { signedIn, isLoading: isAuthLoading, appPath } = useAppSession()
  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [step, setStep] = useState<"email" | "verify">("email")
  const [code, setCode] = useState("")

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      await requestMagicLink(email)
      toast.success("Verification code sent to your email")
      setStep("verify")
    } catch {
      toast.error("Failed to send verification code")
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    try {
      const businessData = JSON.parse(
        sessionStorage.getItem("businessData") || "{}",
      )

      const response = await verifyMagicLink(email, code, businessData)

      if (!response || !response.user) {
        throw new Error("Invalid response from server")
      }

      sessionStorage.removeItem("businessData")

      toast.success("Login successful")

      setTimeout(() => {
        if (response.user.role === "manager") {
          navigate("/dashboard")
        } else if (response.user.onboardingStatus === "pending") {
          navigate("/onboarding")
        } else {
          navigate("/dashboard")
        }
      }, 100)
    } catch (error: unknown) {
      console.error("Verification error:", error)
      const errorMessage =
        (
          error as {
            response?: { data?: { message?: string } }
            message?: string
          }
        )?.response?.data?.message ||
        (error as { message?: string })?.message ||
        "Invalid or expired code"
      toast.error(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  if (isAuthLoading) {
    return <FullPageLoader text="Signing you in" />
  }

  if (signedIn) {
    return <Navigate to={appPath} replace />
  }

  return (
    <AuthLayout>
      <AuthCard>
          <div className="mb-8 text-center">
            <Link to="/" aria-label="Sqaleshop home" className="inline-flex">
              <img src={logo} alt="Sqaleshop" className="mx-auto h-9 w-auto" />
            </Link>
            <h1 className="font-heading mt-6 text-[1.65rem] font-bold tracking-tight text-[#1a1523]">
              Welcome back
            </h1>
            <p className="mt-1.5 text-sm font-medium text-[#6B6B66]">
              Sign in to access your business dashboard.
            </p>
          </div>

          {step === "email" ? (
            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-sm font-medium text-[#292d32]">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@email.com"
                  value={email}
                  className={authFieldClass}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>
              <Button
                type="submit"
                className={authButtonClass}
                disabled={isLoading}
              >
                {isLoading ? "Sending code..." : "Continue with email"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifySubmit} className="space-y-4">
              <div className="flex items-start gap-2 rounded-xl bg-[#f6f4ef] px-3.5 py-3 text-sm font-medium text-[#3b1578]">
                <Mail className="mt-0.5 size-4 shrink-0" />
                <p>
                  We&apos;ve sent a 6-digit code to <strong>{email}</strong>
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="code" className="text-sm font-medium text-[#292d32]">
                  Verification code
                </Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  placeholder="6-digit code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className={authFieldClass}
                  maxLength={6}
                  required
                  autoComplete="one-time-code"
                />
              </div>
              <Button
                type="submit"
                className={authButtonClass}
                disabled={isLoading}
              >
                {isLoading ? "Verifying..." : "Sign in"}
              </Button>
              <Button
                type="button"
                variant="outline"
                className={authOutlineButtonClass}
                onClick={() => setStep("email")}
              >
                Use a different email
              </Button>
            </form>
          )}

          <AuthLegalNotice action="continue" className="mt-6" />

          <p className="mt-4 text-center text-sm font-medium text-[#6B6B66]">
            Don&apos;t have an account?{" "}
            <Link
              to="/register"
              className="font-semibold text-primary underline-offset-4 hover:underline"
            >
              Sign up
            </Link>
          </p>
      </AuthCard>
    </AuthLayout>
  )
}
